import React, { useState } from "react";
import toast from "react-hot-toast";
import { candidateApi } from "../../../API/Api";
import { PhoneInput } from "react-international-phone";

const CandidatesForm = () => {
  const [fileName, setFileName] = useState("No file chosen");
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    location: "",
    position: "",
    experience: "",
    visa: "",
    file: null,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // handle input change
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "file" && files.length > 0) {
      const file = files[0];

      // size check → 2MB (2 * 1024 * 1024)
      if (file.type === "application/pdf" && file.size > 2 * 1024 * 1024) {
        toast.error("PDF file size must be less than 2MB");
        return;
      }
      setFormData({
        ...formData,
        file,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\+?[0-9]{7,15}$/.test(formData.phone)) {
      newErrors.phone = "Phone must be 7–15 digits";
    }
    if (!formData.location) newErrors.location = "Select a location";
    if (!formData.position) newErrors.position = "Select a position";
    if (!formData.experience) newErrors.experience = "Select experience level";
    if (!formData.file) {
      newErrors.file = "Resume is required";
    } else if (formData.file.size > 10 * 1024 * 1024) {
      newErrors.file = "File size must be under 10MB";
    }

    setErrors(newErrors);

    // 🔹 Show toast for first error if any
    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0];
      toast.error(firstError);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return; // 🔹 stop if invalid

    try {
      setLoading(true);
      await candidateApi.submitCandidateForm(formData);
      toast.success("Form submitted successfully 🚀");

      // Reset form
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        location: "",
        position: "",
        experience: "",
        visa: "",
        file: null,
      });
      setFileName("No file chosen");
      setErrors({});
    } catch (err) {
      console.error("❌ API Error:", err);
      toast.error("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container form-section" style={{ padding: 0 }}>
      <div className="form-left col-md-6">
        <form onSubmit={handleSubmit} noValidate className="text-center">
          <h3>Ready for Your Next Big Move?</h3>
          <p>Tell us about your skills and goals, we will do the rest.</p>

          {/* Full Name + Email */}
          <div className="mb-2 d-flex gap-2">
            <div className="w-100">
              <label className="form-label">Full Name*</label>
              <input
                type="text"
                name="fullName"
                className={`form-control ${
                  errors.fullName ? "is-invalid" : ""
                }`}
                value={formData.fullName}
                onChange={handleChange}
              />
              {errors.fullName && (
                <div className="invalid-feedback text-start ">
                  {errors.fullName}
                </div>
              )}
            </div>

            <div className="w-100">
              <label className="form-label">Email Address*</label>
              <input
                type="email"
                name="email"
                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && (
                <div className="invalid-feedback text-start">
                  {errors.email}
                </div>
              )}
            </div>
          </div>

          {/* Phone + Location */}
          <div className="mb-2 d-flex gap-2">
            <div className="w-100">
              <label className="form-label">Phone Number*</label>
              <PhoneInput
                defaultCountry="in"
                value={formData.phone}
                className={`${errors.phone ? "is-invalid" : ""}`}
                onChange={(phone) => setFormData({ ...formData, phone })} // ✅ FIX
                inputStyle={{
                  width: "100%",
                  height: "38px",
                  color: "black",
                  borderRadius: 5,
                  paddingLeft: "10px",
                  border: "2px solid #cbb4f7 ",
                }}
                buttonStyle={{ border: "none", backgroundColor: "#1a1a1a" }}
                containerStyle={{ width: "100%" }}
                enableSearch={true}
              />
              {errors.phone && (
                <div className="invalid-feedback text-start">
                  {errors.phone}
                </div>
              )}
            </div>

            <div className="w-100">
              <label className="form-label">Current Location*</label>
              <select
                name="location"
                className={`form-select ${errors.location ? "is-invalid" : ""}`}
                value={formData.location}
                onChange={handleChange}
              >
                <option value="">Select Country</option>
                <option value="in">🇮🇳 India</option>
                <option value="us">🇺🇸 United States</option>
                <option value="gb">🇬🇧 United Kingdom</option>
                <option value="ca">🇨🇦 Canada</option>
                <option value="au">🇦🇺 Australia</option>
              </select>
              {errors.location && (
                <div className="invalid-feedback text-start">
                  {errors.location}
                </div>
              )}
            </div>
          </div>

          {/* Position + Experience */}
          <div className="mb-2 d-flex gap-2">
            <div className="w-100">
              <label className="form-label">Position Applying For*</label>
              <select
                name="position"
                className={`form-select ${errors.position ? "is-invalid" : ""}`}
                value={formData.position}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option>Frontend Developer</option>
                <option>Backend Developer</option>
              </select>
              {errors.position && (
                <div className="invalid-feedback text-start">
                  {errors.position}
                </div>
              )}
            </div>

            <div className="w-100">
              <label className="form-label">Experience Level*</label>
              <select
                name="experience"
                className={`form-select ${
                  errors.experience ? "is-invalid" : ""
                }`}
                value={formData.experience}
                onChange={handleChange}
              >
                <option value="">Select</option>
                <option>0-1 Years</option>
                <option>1-3 Years</option>
                <option>3-5 Years</option>
              </select>
              {errors.experience && (
                <div className="invalid-feedback text-start">
                  {errors.experience}
                </div>
              )}
            </div>
          </div>

          {/* Visa */}
          <div className="mb-2">
            <label className="form-label">Visa Status (If Applicable)</label>
            <input
              type="text"
              name="visa"
              className="form-control"
              value={formData.visa}
              onChange={handleChange}
            />
          </div>

          {/* File Upload */}
          <div className="mb-2">
            <label className="form-label">Resume, Portfolio, Other Docs*</label>
            <div className="file-upload d-flex align-items-center gap-2">
              <label htmlFor="fileInput" className="btn btn-outline-primary">
                <i className="fas fa-upload"></i>
                <span className="ms-1">Attach a file</span>
              </label>
              <input
                id="fileInput"
                type="file"
                accept=".doc,.docx,.pdf"
                name="file"
                onChange={handleChange}
                style={{ display: "none" }}
              />
              <span className="file-name">{fileName}</span>
            </div>
            {errors.file && (
              <div className="text-danger text-start small">{errors.file}</div>
            )}
            <small className="form-text text-muted">
              Attach .doc, .docx, .pdf files only (Max of 10MB)
            </small>
          </div>

          {/* Submit */}
          <div className="text-center d-flex justify-content-center">
            <button
              type="submit"
              className="submit-btn btn-sm mt-3 employee-hero-btn"
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>

      {/* Right Column */}
      <div className="col-md-6 form-right d-flex flex-column justify-content-center align-items-center">
        <div
          className="heading text-center w-50"
          style={{ marginLeft: "auto" }}
        >
          <h4>Welcome to Anaxis Recruit</h4>
          <p>Your next opportunity could be just a few clicks away.</p>
        </div>
      </div>
      <style jsx>
        {`
          .employee-hero-btn {
            display: block !important;
          }
          [type="button"]:not(:disabled),
          [type="reset"]:not(:disabled),
          [type="submit"]:not(:disabled),
          button:not(:disabled) {
            display: none;
          }
        `}
      </style>
    </div>
  );
};

export default CandidatesForm;
